import React, { useState } from "react";

import Grid from "@material-ui/core/Grid";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Formik, getIn, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";
import { v4 as uuidv4 } from "uuid";

import {
  addBidderRegistration,
  bulkUpdateSaleLotsActionCreator,
} from "actions";

import StateAwareBusinessPICSelector from "components/BusinessPICSelector";
import { SlimButton, SlimSecondaryButton } from "components/Button";
import { Button, SecondaryButton } from "components/Form";
import { BidderNumberInput } from "components/Form/BidderNumberInput";
import { BuyerAwareBuyerWayField } from "components/Form/Fields";
import {
  FormikAwareBusinessPICSelector,
  withNamespace,
} from "components/Form/FormikControls";
import { Tooltip } from "components/Form/FormikControls/Tooltip";
import { Row } from "components/Layout";
import { CompactLotCard } from "components/LotCard/CompactLotCard";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { Subtitle, TitleText } from "components/Text";

import { BUYER } from "constants/businesses";

import { getBuyerInformationString } from "lib/businesses";
import { closeAllHashModals, returnToLocation } from "lib/navigation";

import {
  getBuyerWaysByBusinessId,
  getCurrentSaleyardId,
  getHasWriteAccessInCurrentSale,
  getSaleLotsBySale,
  selectBidderByBuyerInformation,
} from "selectors";

const businessRoles = [BUYER];

const LargeToolTipIcon = styled(FontAwesomeIcon).attrs({
  icon: faQuestionCircle,
  size: "2x",
})(({ theme }) => `color: ${theme.colors.primary};`);

interface UpdateBuyerFormValues {
  bidderNumber: string | number | null;
  buyerId: string | null;
  destinationPropertyId: string | null;
  buyerWay: BuyerWay | null;
  thirdPartyId: string | null;
  invoiceToBusinessId: string | null;
}

interface ClearedBuyerWay {
  buyerWay: BuyerWay;
}

interface UpdateBuyerFormProps {
  namespace?: string;
  buyerId: string;
  clearedBuyerWay: ClearedBuyerWay | null;
  setClearedBuyerWay: (cleared: ClearedBuyerWay | null) => void;
}

function UpdateBuyerForm({
  namespace: ns,
  buyerId,
  clearedBuyerWay,
  setClearedBuyerWay,
}: UpdateBuyerFormProps) {
  const saleyardId = useSelector(getCurrentSaleyardId);

  const formikProps = useFormikContext<UpdateBuyerFormValues>();
  const { setFieldValue, values } = formikProps;

  const buyerWayName = getIn(values, withNamespace(ns, "buyerWay.name"));

  const currentBuyerId = getIn(values, withNamespace(ns, "buyerId"));
  const currentBuyerWays: BusinessBuyerWay[] =
    useSelector(getBuyerWaysByBusinessId(currentBuyerId)) || [];

  function clearBuyerWay(businessId: string) {
    const previousBuyerId = getIn(values, withNamespace(ns, "buyerId"));
    if (businessId !== previousBuyerId) {
      const previousBuyerWay: BuyerWay | null = getIn(
        values,
        withNamespace(ns, "buyerWay"),
      );
      setFieldValue(withNamespace(ns, "buyerWay"), null);
      if (previousBuyerWay) {
        setClearedBuyerWay({ buyerWay: previousBuyerWay });
      }
    }
  }

  function handleKeepBuyerWay() {
    if (!clearedBuyerWay) {
      return;
    }
    const matchingWay = currentBuyerWays.find(
      bw =>
        bw.name.toLowerCase() === clearedBuyerWay.buyerWay.name.toLowerCase(),
    );
    setFieldValue(
      withNamespace(ns, "buyerWay"),
      matchingWay
        ? { id: matchingWay.id, name: matchingWay.name }
        : { id: -Date.now(), name: clearedBuyerWay.buyerWay.name },
    );
    setClearedBuyerWay(null);
  }

  function handleClearBuyerWay() {
    setClearedBuyerWay(null);
  }

  const onClickSetBuyerAsThirdParty = () => {
    // Note, this sets to the INITIAL buyer, so if the user is opening this form up
    // and starts updating, this can be quickly done without having to remember/search.
    setFieldValue(withNamespace(ns, "thirdPartyId"), buyerId);
  };

  const onClickClearThirdParty = () => {
    setFieldValue(withNamespace(ns, "thirdPartyId"), null);
  };

  const hasWriteAccessInCurrentSale = useSelector(
    getHasWriteAccessInCurrentSale,
  );

  return (
    <>
      <Grid xs={12} item>
        <StateAwareBusinessPICSelector
          buyerWayName={buyerWayName}
          label="Buyer"
          businessFieldName={withNamespace(ns, "buyerId")}
          propertyFieldName={withNamespace(ns, "destinationPropertyId")}
          formikProps={formikProps}
          businessRoles={businessRoles}
          onAfterSelect={clearBuyerWay}
          disabled={!hasWriteAccessInCurrentSale}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <BuyerAwareBuyerWayField
          name={withNamespace(ns, "buyerWay")}
          label="Buyer Way"
          buyerField={withNamespace(ns, "buyerId")}
          readOnly={!hasWriteAccessInCurrentSale}
          saleyardId={saleyardId}
        />
      </Grid>

      {clearedBuyerWay && (
        <BuyerWayClearedDialog
          buyerWayName={clearedBuyerWay.buyerWay.name}
          onKeep={handleKeepBuyerWay}
          onClear={handleClearBuyerWay}
        />
      )}

      <Grid item xs={12} sm={6}>
        <StateAwareBusinessPICSelector
          label="Third Party"
          businessFieldName={withNamespace(ns, "thirdPartyId")}
          tooltip="Commission Buyer"
          formikProps={formikProps}
          businessRoles={businessRoles}
          optional
          disabled={!hasWriteAccessInCurrentSale}
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <SlimSecondaryButton
              fullWidth
              disabled={!hasWriteAccessInCurrentSale}
              onClick={onClickClearThirdParty}
              type="button"
            >
              Remove 3rd Party Buyer
            </SlimSecondaryButton>
          </Grid>
          <Grid item xs={6}>
            <SlimButton
              disabled={!hasWriteAccessInCurrentSale}
              fullWidth
              onClick={onClickSetBuyerAsThirdParty}
              type="button"
            >
              Set as original buyer
            </SlimButton>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} sm={6}>
        <BidderNumberInput disabled={!hasWriteAccessInCurrentSale} />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormikAwareBusinessPICSelector
          label="Invoice To"
          businessFieldName={withNamespace(ns, "invoiceToBusinessId")}
          tooltip="Invoice To"
          businessRoles={businessRoles}
          optional
          clearable
        />
      </Grid>
    </>
  );
}

interface BuyerWayClearedDialogProps {
  buyerWayName: string;
  onKeep: () => void;
  onClear: () => void;
}

function BuyerWayClearedDialog({
  buyerWayName,
  onKeep,
  onClear,
}: BuyerWayClearedDialogProps) {
  return (
    <Dialog open maxWidth="xs" fullWidth>
      <DialogTitle>Buyer Way Action</DialogTitle>
      <DialogContent dividers>
        <Row justifyBetween alignCenter>
          <Subtitle inline>Original Buyer Way</Subtitle>
          <Tooltip
            title={`You've changed the buyer. Hit "Keep" to keep "${buyerWayName}" on the new buyer, or "Clear" to leave it blank.`}
          >
            <LargeToolTipIcon />
          </Tooltip>
        </Row>
        <TitleText>{buyerWayName}</TitleText>
      </DialogContent>
      <DialogActions>
        <SecondaryButton
          type="button"
          data-tour="clear-buyer-way"
          onClick={onClear}
        >
          Clear
        </SecondaryButton>
        <Button type="button" data-tour="keep-buyer-way" onClick={onKeep}>
          Keep
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface UpdateBuyerActionsProps {
  onClickClose: () => void;
}

function UpdateBuyerActions({ onClickClose }: UpdateBuyerActionsProps) {
  const { dirty, isValid } = useFormikContext();

  const isSubmitDisabled = !(dirty && isValid);

  return (
    <DialogActions>
      <Button onClick={onClickClose}>Back</Button>
      <Button type="submit" disabled={isSubmitDisabled}>
        Submit
      </Button>
    </DialogActions>
  );
}

interface UpdateBuyerModalProps {
  buyerId: string;
  destinationPropertyId: string | null;
  buyerWayId?: number | string | null;
  thirdPartyId: string | null;
  invoiceToBusinessId: string | null;
  returnTo?: string;
}

export const UpdateBuyerModal = ({
  buyerId,
  destinationPropertyId,
  buyerWayId,
  thirdPartyId,
  invoiceToBusinessId,
  returnTo,
}: UpdateBuyerModalProps) => {
  const dispatch = useDispatch();

  const [clearedBuyerWay, setClearedBuyerWay] =
    useState<ClearedBuyerWay | null>(null);

  const closeSelf = () => {
    if (returnTo) {
      returnToLocation(returnTo);
    } else {
      closeAllHashModals();
    }
  };

  const buyerWays = useSelector(getBuyerWaysByBusinessId(buyerId)) || [];

  const boughtLots: SaleLot[] = useSelector(state => {
    return getSaleLotsBySale(state).filter(
      (sl: SaleLot) =>
        sl.buyer_id === buyerId &&
        ((sl.buyer_way === null && !buyerWayId) ||
          sl.buyer_way?.id === buyerWayId) &&
        ((!sl.thirdPartyId && !thirdPartyId) ||
          sl.thirdPartyId === thirdPartyId),
    );
  });

  const bidderByBuyerInformationLookup = useSelector(
    selectBidderByBuyerInformation,
  );

  const buyerWay = buyerWays.find(
    (bw: BusinessBuyerWay) => bw.id === buyerWayId,
  );

  const buyerInformationString = getBuyerInformationString(
    buyerId,
    destinationPropertyId,
    buyerWay?.name,
  );

  const bidder: BidderRegistration | undefined =
    bidderByBuyerInformationLookup[buyerInformationString];

  const initialValues: UpdateBuyerFormValues = {
    bidderNumber: bidder ? bidder.registrationNumber : null,
    buyerId,
    destinationPropertyId,
    buyerWay,
    thirdPartyId,
    invoiceToBusinessId,
  };

  const handleSubmit = (values: UpdateBuyerFormValues) => {
    let currentBuyerWay: BuyerWay | null = null;
    if (
      values.buyerWay &&
      values.buyerWay.id &&
      values.buyerWay.name &&
      values.buyerWay.id !== -1
    ) {
      currentBuyerWay = values.buyerWay;
    }

    const bulkPayload = boughtLots.map(sl => ({
      buyer_id: values.buyerId,
      destination_property_id: values.destinationPropertyId,
      buyer_way: currentBuyerWay,
      thirdPartyId: values.thirdPartyId,
      invoiceToBusinessId: values.invoiceToBusinessId,
      id: sl.id,
    }));

    dispatch(
      bulkUpdateSaleLotsActionCreator(bulkPayload, {
        changeReason: "Bulk updated buyer",
      }),
    );

    if (values.bidderNumber !== initialValues.bidderNumber) {
      const submittedBuyerInformationString = getBuyerInformationString(
        values.buyerId,
        values.destinationPropertyId,
        values.buyerWay?.name,
      );

      const associatedBidder =
        bidderByBuyerInformationLookup[submittedBuyerInformationString];

      if (!associatedBidder) {
        // if a bidder does not exist for the current buyer/way/pic combination, create one
        dispatch(
          addBidderRegistration(uuidv4(), {
            // Bidder Number is a plain text field, so it may still be a
            // string here even though registrationNumber is numeric
            // server-side.
            registrationNumber: values.bidderNumber as unknown as number,
            businessId: values.buyerId ?? undefined,
            defaultPropertyId: values.destinationPropertyId ?? undefined,
            buyerWay: values?.buyerWay?.name,
          }),
        );
      }
    }
    closeSelf();
  };

  return (
    <ZoomyDialog open onClose={closeSelf} maxWidth="sm" fullWidth>
      <DialogTitle onClose={closeSelf}>
        Bulk Update Buyer for {boughtLots.length} Sale Lot
        {boughtLots.length !== 1 && "s"}
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <Form>
          <DialogContent dividers shrink={0}>
            <Grid container spacing={1}>
              <UpdateBuyerForm
                buyerId={buyerId}
                clearedBuyerWay={clearedBuyerWay}
                setClearedBuyerWay={setClearedBuyerWay}
              />
            </Grid>
          </DialogContent>
          <DialogContent dividers>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                {boughtLots.map(saleLot => (
                  <CompactLotCard {...saleLot} key={saleLot.id} />
                ))}
              </Grid>
            </Grid>
          </DialogContent>

          <UpdateBuyerActions onClickClose={closeSelf} />
        </Form>
      </Formik>
    </ZoomyDialog>
  );
};
